import { useCallback, useMemo, useState } from "react";

import {
  type CloudFlowNodeType,
  type NodeConfigNotificationParameters,
  NotificationProviders,
} from "@doitintl/cmp-models";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import noop from "lodash/noop";
import type * as yup from "yup";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { useToggle } from "../../../../../../utils/useToggle";
import { ReferencedTextAreaStandalone } from "../../../../Components/ReferencedTextArea/ReferencedTextArea";
import { useReferencedFieldContext } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { FormChangesListener } from "../../../Common/FormChangesListener";
import { useNodeConfigurationContext } from "../../NodeConfigurationContext";
import { SlackNotificationProvider } from "../Approval/NotificationProvider";
import { useErrorUpdates, useInitialErrors, useSlackChannels } from "../hooks";
import { ListTagDocumentationModal } from "./ListTagDocumentationModal";
import { generateNotificationSchema } from "./schema";

export const NotificationForm = () => {
  const { nodeConfig, updateNode } = useNodeConfigurationContext<CloudFlowNodeType.NOTIFICATION>();
  const { referenceableNodes } = useReferencedFieldContext();

  const schema = useMemo(() => generateNotificationSchema(referenceableNodes), [referenceableNodes]);

  const [isValid, setIsValid] = useState(nodeConfig.status !== "error");
  const isSlackProvider = nodeConfig.parameters.notificationProvider === NotificationProviders.SLACK;
  const { slackChannels, channelsLoading } = useSlackChannels(isSlackProvider);
  const [isDocModalOpen, showDocModal, hideDocModal] = useToggle(false);

  const initialValues = schema.cast(nodeConfig.parameters);
  const initialErrors = useInitialErrors(schema, initialValues);

  useErrorUpdates<CloudFlowNodeType.NOTIFICATION>({
    isValid,
    errorKey: "param_error",
    errorMessage: cloudflowTexts.STATUS.INCORRECT_FORM_PARAMETERS_ERROR,
  });

  const onValuesChange = useCallback(
    (values: yup.InferType<typeof schema>) => {
      const parameters = schema.cast(values) as NodeConfigNotificationParameters;
      updateNode((prev) => ({ ...prev, parameters }));
    },
    [updateNode, schema]
  );

  return (
    <>
      <Formik<yup.InferType<typeof schema>>
        validateOnChange
        validateOnBlur
        validateOnMount
        initialValues={initialValues}
        validationSchema={schema}
        initialErrors={initialErrors}
        onSubmit={noop}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched }) => (
          <Form>
            <Stack gap={2}>
              <Stack gap={0.5}>
                <Typography variant="subtitle2" fontWeight={500}>
                  {cloudflowTexts.NOTIFICATION_SELECT}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cloudflowTexts.NOTIFICATION_SELECT_OPTIONS}
                </Typography>
              </Stack>

              <FormControl fullWidth size="small" required>
                <InputLabel>{cloudflowTexts.NOTIFICATION_PROVIDER}</InputLabel>
                <Select
                  name="notificationProvider"
                  label="Notification provider"
                  value={values.notificationProvider}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.notificationProvider && Boolean(errors.notificationProvider)}
                >
                  <MenuItem value={NotificationProviders.SLACK}>Slack</MenuItem>
                  <MenuItem value={NotificationProviders.EMAIL}>Email</MenuItem>
                </Select>
                {errors.notificationProvider !== undefined && (
                  <FormHelperText error={true}>{errors.notificationProvider}</FormHelperText>
                )}
              </FormControl>

              {values.notificationProvider === NotificationProviders.SLACK && (
                <SlackNotificationProvider
                  loadingSlackChannels={channelsLoading}
                  slackChannels={slackChannels}
                  slackChannelId={values.slackChannel?.id}
                  onSlackChannelChange={(e) => {
                    const selectedChannel = slackChannels.find((channel) => channel.id === e.target.value);
                    if (selectedChannel) {
                      setFieldValue("slackChannel", selectedChannel);
                    }
                  }}
                />
              )}

              {values.notificationProvider === NotificationProviders.EMAIL && (
                <FormControl fullWidth size="small" required>
                  <TextField
                    name="emailRecipient"
                    label="Email"
                    value={values.emailRecipient}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.emailRecipient && Boolean(errors.emailRecipient)}
                    helperText={touched.emailRecipient && errors.emailRecipient}
                    required
                  />
                </FormControl>
              )}

              <Stack gap={0.5}>
                <Typography variant="subtitle2" fontWeight={500}>
                  Message
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cloudflowTexts.NOTIFICATION_USE_REFERENCES}
                </Typography>
              </Stack>

              <ReferencedTextAreaStandalone
                onChange={(e) => {
                  setFieldValue("message", e.target.value);
                }}
                onBlur={() => {
                  // TODO: michal fix ReferencedTextArea to support Formik's generic handleBlur method
                  setFieldTouched("message", true);
                }}
                referenceableNodes={referenceableNodes}
                label="Message"
                name="message"
                value={values.message}
                required={true}
                error={touched.message && Boolean(errors.message)}
                helperText={touched.message && errors.message ? errors.message : undefined}
              />

              <Stack direction="column" gap={0.5} alignItems={"flex-start"}>
                <Typography variant="body2" color="text.secondary">
                  TIP: Use a {"<list>"} tag to present multiple references together
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={showDocModal}
                  sx={{ textDecoration: "none" }}
                >
                  Learn more
                </Link>
              </Stack>
            </Stack>
            <FormChangesListener onValidityChange={setIsValid} onValuesChange={onValuesChange} />
          </Form>
        )}
      </Formik>
      <ListTagDocumentationModal open={isDocModalOpen} onClose={hideDocModal} />
    </>
  );
};
